import {useMemo, useEffect} from 'react';
import {useCoursePromoVideoQuery} from '@/generated/graphql';
import {Course} from '@udemy-v2/browse-course';

interface UseCoursePromoVideoResult extends Pick<Course, 'promoVideo'> {
    isLoading: boolean;
    error: unknown;
    refetch: () => void;
}

/**
 * Hook that wraps useCoursePromoVideoQuery and handles automatic refetching
 * based on the expiresOn field to ensure fresh promo video data.
 *
 * @param options - Configuration options for the hook
 * @returns Object containing promo video data, loading state, error, and refetch function
 */
export function useCoursePromoVideo({id}: Pick<Course, 'id'>): UseCoursePromoVideoResult {
    const {data, isLoading, error, refetch} = useCoursePromoVideoQuery(
        {courseId: id},
        {
            // Set a reasonable stale time to prevent unnecessary refetches
            staleTime: 5 * 60 * 1000, // 5 minutes
        },
    );

    const promoVideo = useMemo(() => {
        const graphqlPromoVideo = data?.course?.promoVideo;
        if (!graphqlPromoVideo) {
            return undefined;
        }

        // Convert GraphQL CoursePromoVideo to Course['promoVideo'] format
        return {
            url: graphqlPromoVideo.url,
            expiresOn: graphqlPromoVideo.expiresOn,
        };
    }, [data?.course?.promoVideo]);

    // Calculate TTL and set up automatic refetch based on expiresOn
    useEffect(() => {
        if (!promoVideo?.expiresOn) {
            return;
        }

        // Convert expiresOn to milliseconds (assuming it's a Unix timestamp)
        const expiresOnMs =
            typeof promoVideo.expiresOn === 'number'
                ? promoVideo.expiresOn * 1000
                : new Date(promoVideo.expiresOn).getTime();

        const now = Date.now();
        // Add a 1-minute buffer to account for clock differences and ensure fresh data
        const bufferMs = 60 * 1000; // 1 minute
        const timeUntilRefetch = expiresOnMs - now - bufferMs;

        // If already expired or within buffer time, refetch immediately
        if (timeUntilRefetch <= 0) {
            refetch();
            return;
        }

        // Set up automatic refetch 1 minute before the video expires
        const timeoutId = setTimeout(() => {
            refetch();
        }, timeUntilRefetch);

        // Cleanup timeout on unmount or when dependencies change
        return () => {
            clearTimeout(timeoutId);
        };
    }, [promoVideo?.expiresOn, refetch]);

    return {
        promoVideo,
        isLoading,
        error,
        refetch,
    };
}
